/**
 * UserRole - Defines user roles for role-based access control
 *
 * Maps to backend: ASP.NET Identity Roles
 * - Admin: Full system access
 * - BusinessOwner: Business owner with access to their own business data
 * - NonProfitUser: Non-profit organization user with access to phrase matches and dashboard
 * - MineMinerRep: Epic-006 MineMiner rep (sprint-069) — read access to MineMiner leads/health
 * - MineMinerAdmin: Epic-006 MineMiner admin (sprint-069) — implicitly satisfies MineMinerRep
 *
 * sprint-217 (epic-036 Phase 1 increment 1D) — the three CLIENT roles below join them,
 * mirroring `MineMiner.Domain.Identity.MineMinerRoles`. They are mine-scoped personas held
 * by our customers' own staff, and they are NOT a tier of the staff roles above: a client
 * Admin is a different person from a MineMinerAdmin, who is internal product staff.
 *
 * 🔴 A client role name on its own grants NOTHING. Every client-facing authorisation
 * decision resolves through a MineMembership row for the specific mine, server-side
 * (DR-005 clause 2). These names exist here so the SPA can render the right chrome.
 */
export type UserRole =
  | 'Admin'
  | 'BusinessOwner'
  | 'NonProfitUser'
  | 'MineMinerRep'
  | 'MineMinerAdmin'
  | 'MineClientAdmin'
  | 'MineClientSupervisor'
  | 'MineClientLegal'
  | 'OrganizationAdmin'
  | 'OrganizationUser';

/**
 * The three mine-scoped client roles (sprint-217). Mirrors `MineMinerRoles.ClientAll`.
 */
export const MINE_CLIENT_ROLES = [
  'MineClientAdmin',
  'MineClientSupervisor',
  'MineClientLegal',
] as const;

export type MineClientRole = (typeof MINE_CLIENT_ROLES)[number];

/**
 * The two organization-scoped roles (sprint-239, epic-039 Canary increment A). Mirrors
 * `MineMinerRoles.OrganizationAll`.
 *
 * 🔴 A THIRD tenancy lane, not a tier of the mine-client roles above (DR-002). An
 * organization watches a portfolio of MSHA entities it does not operate; a mine client
 * operates mines. Holding one grants nothing in the other, here or on the server.
 */
export const ORGANIZATION_ROLES = ['OrganizationAdmin', 'OrganizationUser'] as const;

export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

const ALL_USER_ROLES: readonly UserRole[] = [
  'Admin',
  'BusinessOwner',
  'NonProfitUser',
  'MineMinerRep',
  'MineMinerAdmin',
  ...MINE_CLIENT_ROLES,
  ...ORGANIZATION_ROLES,
];

/**
 * Type guard to check if a string is a valid UserRole
 */
export function isUserRole(value: string): value is UserRole {
  return (ALL_USER_ROLES as readonly string[]).includes(value);
}

/**
 * True when the role is one of the three mine-scoped client roles.
 *
 * Mirrors `MineMinerRoles.IsClientRole`. Used to decide which area of the SPA a signed-in
 * user belongs in — never to decide whether they may see a particular mine's data.
 */
export function isMineClientRole(value: string | null | undefined): value is MineClientRole {
  return typeof value === 'string' && (MINE_CLIENT_ROLES as readonly string[]).includes(value);
}

/**
 * True when the role is one of the two organization-scoped roles.
 *
 * Mirrors `MineMinerRoles.IsOrganizationRole`. Used to decide which area of the SPA a
 * signed-in user belongs in — never to decide whether they may see a particular
 * organization's data, which is resolved server-side against a live OrganizationMembership
 * row on every request.
 */
export function isOrganizationRole(value: string | null | undefined): value is OrganizationRole {
  return typeof value === 'string' && (ORGANIZATION_ROLES as readonly string[]).includes(value);
}

/**
 * Parse a string to UserRole with fallback
 */
export function parseUserRole(value: string | null | undefined): UserRole | null {
  if (!value) {return null;}
  return isUserRole(value) ? value : null;
}

/**
 * Pick the single role that decides which area of the app a user lands in.
 *
 * sprint-217. The login response returns `roles` as an unordered array, so reading
 * `roles[0]` picks arbitrarily when a user holds more than one. Staff roles win over
 * client roles: an internal admin who also holds a client role for support work belongs in
 * the staff tool, and the client area is the narrower surface.
 *
 * Returns null when the user holds no role this SPA recognises. 🔴 Callers must NOT
 * substitute a staff role for null — before sprint-217 this function's caller defaulted to
 * `MineMinerRep`, which labelled every client user as internal staff and had
 * `MineMinerRoute` wave them straight into the staff CRM.
 */
export function primaryUserRole(roles: readonly string[] | null | undefined): UserRole | null {
  if (!roles || roles.length === 0) {return null;}

  const precedence: readonly UserRole[] = [
    'Admin',
    'MineMinerAdmin',
    'MineMinerRep',
    'MineClientAdmin',
    'MineClientSupervisor',
    'MineClientLegal',
    // sprint-239 — the organization lane sits below the mine-client lane for the same reason
    // the client lane sits below staff: when someone holds roles in two areas, the narrower
    // surface is the safer landing. Nobody should hold both in practice (the invite handler
    // refuses it), so this ordering is a backstop rather than a routine path.
    'OrganizationAdmin',
    'OrganizationUser',
    'BusinessOwner',
    'NonProfitUser',
  ];

  return precedence.find((candidate) => roles.includes(candidate)) ?? null;
}

/**
 * Get display-friendly label for UserRole
 */
export function getUserRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    Admin: 'Administrator',
    BusinessOwner: 'Business Owner',
    NonProfitUser: 'Non-Profit User',
    MineMinerRep: 'MineMiner Rep',
    MineMinerAdmin: 'MineMiner Admin',
    MineClientAdmin: 'Mine Admin',
    MineClientSupervisor: 'Supervisor',
    MineClientLegal: 'Legal Review',
    OrganizationAdmin: 'Organization Admin',
    OrganizationUser: 'Organization Member'
  };
  return labels[role];
}
